import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

import { isPdfUrl } from '@shared/utils/media-url';
import { Button } from '@shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogTitle,
  DialogHeader,
  DialogContent,
  DialogDescription,
} from '@shared/components/ui/dialog';

import { MediaPreview } from './media-preview';

type Props = {
  medias: string[];
  /** Index of the visual shown, null when the dialog is closed. */
  index: number | null;
  onIndexChange: (index: number | null) => void;
  title: string;
};

/** The visuals at full size, one at a time: arrows (buttons or keyboard ← →) go through them when there are several. */
export const MediaViewerDialog = ({ medias, index, onIndexChange, title }: Props) => {
  const open = index !== null && index < medias.length;
  const current = open ? index : 0;
  const url = medias[current];
  const several = medias.length > 1;

  const go = (step: number) => onIndexChange((current + step + medias.length) % medias.length);

  return (
    <Dialog onOpenChange={(next) => !next && onIndexChange(null)} open={open}>
      <DialogContent
        className="max-w-[min(1100px,calc(100%-2rem))]"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          // The dialog is portaled but React events still bubble to the card that opened it (a checkbox row…).
          event.stopPropagation();
          if (!several) {
            return;
          }
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            go(-1);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            go(1);
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {several ? `Visuel ${current + 1} / ${medias.length}` : 'Visuel à personnaliser'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4">
          {url && <MediaPreview alt={`${title}, visuel ${current + 1}`} key={url} url={url} variant="full" />}
          <div className="flex items-center gap-3">
            {several && (
              <>
                <Button aria-label="Visuel précédent" onClick={() => go(-1)} size="icon" variant="outline">
                  <ChevronLeft />
                </Button>
                <span className="text-sm text-muted-foreground tabular-nums">
                  {current + 1} / {medias.length}
                </span>
                <Button aria-label="Visuel suivant" onClick={() => go(1)} size="icon" variant="outline">
                  <ChevronRight />
                </Button>
              </>
            )}
            {url && (
              <Button asChild className="ml-auto" variant="ghost">
                <a href={url} rel="noreferrer" target="_blank">
                  <ExternalLink />
                  {isPdfUrl(url) ? 'Ouvrir le PDF' : 'Ouvrir dans un onglet'}
                </a>
              </Button>
            )}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};
